import type { BadgeColor } from '../components/Badge/Badge';

/**
 * Project type (billing tier) helpers. Use for consistent labels, Badge colors,
 * and bar/color classes across ProjectCard, tables, and filters.
 */

export const BillingTier = {
  Draft: 'draft',
  Proposal: 'proposal',
  Project: 'project',
  ProjectPlus: 'projectPlus',
} as const;

export type TBillingTier = (typeof BillingTier)[keyof typeof BillingTier];

/** Badge color per billing tier: draft -> grey, proposal -> blue, project/projectPlus -> orange */
export type BillingTierVariant = Extract<BadgeColor, 'grey' | 'blue' | 'orange'>;

// Map billingTier to CSS color class for bars/indicators (theme colors)
// draft -> grey-40, proposal -> blue-50, project/projectPlus -> orange-50
export function getBillingTierColorClass(billingTier: TBillingTier): string {
  if (billingTier === BillingTier.Draft) {
    return 'bg-grey-40';
  }
  if (billingTier === BillingTier.Proposal) {
    return 'bg-blue-40';
  }
  if (billingTier === BillingTier.Project || billingTier === BillingTier.ProjectPlus) {
    return 'bg-orange-40';
  }
  return 'bg-grey-40';
}

export function getBillingTierVariant(billingTier: TBillingTier): BillingTierVariant {
  switch (billingTier) {
    case BillingTier.Draft:
      return 'grey';
    case BillingTier.Proposal:
      return 'blue';
    case BillingTier.Project:
    case BillingTier.ProjectPlus:
      return 'orange';
    default:
      return 'grey';
  }
}

export function formatBillingTierLabel(billingTier: TBillingTier): string {
  switch (billingTier) {
    case BillingTier.Draft:
      return 'Draft';
    case BillingTier.Proposal:
      return 'Project Lite';
    case BillingTier.Project:
      return 'Project';
    case BillingTier.ProjectPlus:
      return 'Project Plus';
    default:
      return 'Project';
  }
}
